import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Button, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import AssociatedDocumentForm from './AssociatedDocumentForm';
import { addAssociatedDocumentThunk, updateAssociatedDocumentThunk } from '../../../slices/thunks';
import { resetAssociatedDocumentFlags } from '../../../slices/associatedDocument/reducer';
import { uploadFiles } from '../../../helpers/file_manager/backend_helper';

const FILE_TYPE_ID = '6669bc0dd0638675e8100fd4';

const WaterRightDocumentForm = ({ waterRightData, dataToEdit, isOpen, handleToggleModal }) => {
	const dispatch = useDispatch();
	const formRef = useRef(null);
	const [isUploading, setIsUploading] = useState(false);

	// Redux selector
	const selectorAssociatedDocument = createSelector(
		state => state.AssociatedDocument,
		associatedDocument => ({
			isAdding: associatedDocument?.isAdding,
			isAdded: associatedDocument?.isAdded,
			isUpdating: associatedDocument?.isUpdating,
			isUpdated: associatedDocument?.isUpdated,
			isCloseModal: associatedDocument?.isCloseModal,
		})
	);

	const { isAdding, isAdded, isUpdating, isUpdated, isCloseModal } = useSelector(selectorAssociatedDocument);

	// Auto-close modal on success
	useEffect(() => {
		if (isCloseModal && (isAdded || isUpdated)) {
			handleToggleModal();
			// Reset flags after a small delay to allow Panel to detect requireRefetch
			setTimeout(() => {
				dispatch(resetAssociatedDocumentFlags());
			}, 100);
		}
	}, [isCloseModal, isAdded, isUpdated, handleToggleModal, dispatch]);

	const handleSubmit = async (values) => {
		if (!values.water_right_id) {
			toast.error('Error: No se pudo identificar el derecho de agua');
			return;
		}

		try {
			if (dataToEdit?._id) {
				// EDIT MODE - Only update metadata (name and document_type)
				const submitData = {
					name: values.name,
					document_type_id: values.document_type_id,
					water_right_id: values.water_right_id,
					file_id: dataToEdit?.file_id,
				};

				dispatch(updateAssociatedDocumentThunk({ 
					id: dataToEdit._id, 
					data: submitData 
				}));
			} else {
				// CREATE MODE - 2-step process: upload file, then create document
				setIsUploading(true);

				// Step 1: Upload file to file manager
				const formData = new FormData();
				formData.append('files', values.file);

				const uploadResponse = await uploadFiles({
					formData: formData,
				});

				if (!uploadResponse || !uploadResponse.length || !uploadResponse[0]._id) {
					toast.error('Error al subir el archivo');
					setIsUploading(false);
					return;
				}

				// Step 2: Create AssociatedDocument with file_id reference
				const submitData = {
					name: values.name,
					water_right_id: values.water_right_id,
					document_type_id: values.document_type_id,
					file_id: uploadResponse[0]._id,
				};

				dispatch(addAssociatedDocumentThunk(submitData));
				setIsUploading(false);
			}
		} catch (error) {
			console.error('Error in form submission:', error);
			toast.error('Error al procesar el documento');
			setIsUploading(false);
		}
	};

	const handleConfirm = () => {
		if (formRef.current) {
			formRef.current.handleSubmit();
		}
	};

	const isProcessing = isAdding || isUpdating || isUploading;

	return (
		<>
			<ModalBody className='modal-body'>
				<AssociatedDocumentForm
					ref={formRef}
					onSubmit={handleSubmit}
					dataToEdit={dataToEdit}
					waterRightId={waterRightData?._id}
				/>
			</ModalBody>

			<ModalFooter>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}} className='btn-danger btn-label'>
						<i className="ri-close-line label-icon"></i>
						Cancelar
					</Button>
					<Button
						type='button'
						color='primary'
						className='btn-label'
						onClick={handleConfirm}
						disabled={isProcessing}
					>
						{isProcessing ? (
							<i className='label-icon'>
								<Spinner size='sm' />
							</i>
						) : (
							<i className='ri-save-line label-icon'></i>
						)}
						Guardar Documento
					</Button>
				</div>
			</ModalFooter>
		</>
	);
};

export default WaterRightDocumentForm;
